import { Component, OnInit } from "@angular/core";
import { AuthService } from "src/app/shared/services/authentication/auth.service";
import { PlaysService } from "src/app/shared/services/plays/plays.service";
import { PayService } from "src/app/shared/services/pay.service";

interface Transaction {
  id: string;
  date: Date;
  type: string;
  description: string;
  amount: number;
  status: "unpaid" | "pending" | "paid";
  originalType: "sale" | "subscription";
}

@Component({
  selector: "app-money",
  templateUrl: "./subscriptions.component.html",
  styleUrls: ["./subscriptions.component.scss"],
})
export class MoneyComponent implements OnInit {
  currentUser: any;
  transactions: Transaction[] = [];
  isLoading = true;

  // Summary stats
  totalEarnings = 0;
  pendingPayout = 0;
  payoutPercentage = 0;
  netPayout = 0;
  lastPayoutDate: string | null = null;

  // Payout request state
  payoutRequesting = false;
  canRequestPayout = false;
  hasPendingPayouts = false;

  constructor(
    private authService: AuthService,
    private playsService: PlaysService
  ) {}

  ngOnInit(): void {
    this.authService.ui$.subscribe((user) => {
      if (user) {
        this.currentUser = user;
        this.loadFinancialData();
      }
    });
  }

  async loadFinancialData(): Promise<void> {
    this.isLoading = true;
    try {
      const [sales, subscriptions] = await Promise.all([
        this.playsService.getSalesByCreator(this.currentUser.id),
        this.playsService.getSubscriptionPaymentsByCreator(this.currentUser.id),
      ]);

      const salesTransactions: Transaction[] = sales.map((sale) => ({
        id: sale.id,
        date: sale.date.toDate(), // Convert Firestore timestamp to JS Date
        type: sale.item.type,
        description:
          sale.item.type !== "ticket"
            ? sale.item.title
            : `${sale.item.title} (x${sale.item.quantity})`,
        amount: sale.item.price * sale.item.quantity,
        status: sale.status || "unpaid",
        originalType: "sale",
      }));

      const subscriptionTransactions: Transaction[] = subscriptions.map(
        (sub) => ({
          id: sub.id,
          date: new Date(sub.date), // Ensure subscription date is a JS Date
          type: "Subscription",
          description: `Subscription from ${sub.userId || "a user"} for ${
            sub.tier
          } tier`,
          amount: sub.price,
          status: sub.status || "unpaid",
          originalType: "subscription",
        })
      );

      this.transactions = [
        ...salesTransactions,
        ...subscriptionTransactions,
      ].sort((a, b) => b.date.getTime() - a.date.getTime());

      this.calculateSummary();
      this.hasPendingPayouts = this.transactions.some(
        (t) => t.status === "pending"
      );
    } catch (error) {
      console.error("Error loading financial data:", error);
    } finally {
      this.isLoading = false;
    }
  }

  calculateSummary(): void {
    this.totalEarnings = this.transactions.reduce(
      (sum, t) => sum + t.amount,
      0
    );
    // Only sum up transactions that haven't been paid or requested for payout
    this.pendingPayout = this.transactions
      .filter((t) => t.status === "unpaid")
      .reduce((sum, t) => sum + t.amount, 0);

    this.setPayoutPercentage();
    this.netPayout = this.pendingPayout * (this.payoutPercentage / 100);

    this.canRequestPayout = this.pendingPayout >= 50; // Payout threshold of $50
    this.lastPayoutDate = this.currentUser.lastPayoutDate || null;
  }

  private setPayoutPercentage(): void {
    const creatorType = this.currentUser?.creatorType?.toLowerCase(); // e.g., 'creator', 'verified', 'premium'
    switch (creatorType) {
      case "premium":
        this.payoutPercentage = 95;
        break;
      case "verified":
        this.payoutPercentage = 90;
        break;
      default: // Includes 'creator' and any other default cases
        this.payoutPercentage = 80;
        break;
    }
  }

  async requestPayout(): Promise<void> {
    if (!this.canRequestPayout || this.payoutRequesting) return;

    const transactionsToPay = this.transactions.filter(
      (t) => t.status === "unpaid"
    );

    if (confirm(`Request payout for $${this.pendingPayout.toFixed(2)}?`)) {
      this.payoutRequesting = true;
      try {
        await this.playsService.requestCreatorPayout(
          this.currentUser.id,
          this.pendingPayout,
          transactionsToPay
        );
        await this.loadFinancialData(); // Reload data to get fresh statuses
        alert("Payout request submitted successfully!");
      } catch (error) {
        console.error("Error requesting payout:", error);
        alert(
          "There was an error submitting your payout request. Please try again."
        );
      } finally {
        this.payoutRequesting = false;
      }
    }
  }
}
